import { HttpStatusCode } from "axios";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import {
  apiDataFetchingAtom,
  apiDataFetchingErrorsAtom,
  ApiException,
  castErrorResult,
  createErrorResultFromException,
  createErrorResultFromTranslatableItem,
  createSuccessResult,
  errors,
  GenericUploadApi,
  IApiError,
  IGenericUploadPrepareDto,
  IGenericUploadPrepResult,
  Log,
  normalizeErrors,
  RidesApi,
  RydeUiUnit,
  SimpleResult,
  simpleResultErrorEmptyResponse,
  toForbiddenTranslatableItem,
  translateInfoItem,
} from "@ryde/shared";

export const useGenericUploadActions = (entity: RydeUiUnit) => {
  const setErrors = useSetRecoilState(apiDataFetchingErrorsAtom);
  const setLoading = useSetRecoilState(apiDataFetchingAtom);
  const { t } = useTranslation();

  const sendUploadPrepareRequest = (payload: IGenericUploadPrepareDto) => {
    if (entity === "ride") {
      return RidesApi.ridesUploadPrepare({ body: payload });
    }

    return GenericUploadApi.prepare({ body: payload });
  };

  const genericUploadPrepare = useEvent(
    async (
      payload: IGenericUploadPrepareDto,
    ): Promise<SimpleResult<IGenericUploadPrepResult>> => {
      try {
        setLoading(true);
        const genericUploadPrepareResult =
          await sendUploadPrepareRequest(payload);

        if (!genericUploadPrepareResult) {
          return simpleResultErrorEmptyResponse(t);
        }

        if (
          !genericUploadPrepareResult?.success ||
          !genericUploadPrepareResult.data
        ) {
          return castErrorResult(genericUploadPrepareResult);
        }

        Log.debug(
          "Generic Upload Prepare dto received: ",
          genericUploadPrepareResult.data,
        );

        return createSuccessResult(genericUploadPrepareResult.data);
      } catch (error) {
        if ((error as ApiException)?.statusCode === HttpStatusCode.Forbidden) {
          const errorDetails = toForbiddenTranslatableItem(
            error,
            errors.common.dontHavePermissionForAction,
          );
          setErrors([translateInfoItem(errorDetails, t)]);
          return createErrorResultFromTranslatableItem({
            translatableItem: errorDetails,
          });
        }

        const normalizedError = normalizeErrors(error as IApiError, t);
        setErrors(normalizedError);
        return createErrorResultFromException(normalizedError);
      } finally {
        setLoading(false);
      }
    },
  );

  return {
    genericUploadPrepare,
  };
};
